import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

import { CatalogListCardBody } from './CatalogListCardBody'
import {
  CATALOG_LIST_CARD_BOTTOM_ROW_CLASS,
  CATALOG_LIST_CARD_CTA_CLASS,
  CATALOG_LIST_CARD_META_ROW_CLASS,
  CATALOG_LIST_CARD_SUMMARY_CLASS,
  CATALOG_LIST_CARD_TITLE_CLASS,
} from './catalogListCardShell'

type CatalogListCardContentProps = {
  className?: string
  title: ReactNode
  summary?: ReactNode | null
  /** 底栏左侧：积分、统计等（与「查看详情」同一行） */
  meta?: ReactNode
  footerLabel?: string
  hideFooter?: boolean
  /** 底栏右侧操作（如编辑/删除图标），与 meta 同一行 */
  footer?: ReactNode
  /** 底栏全宽区块（如资产标签 + 按钮），占满正文底部 */
  footerBlock?: ReactNode
}

function shouldShowSummary(summary?: ReactNode | null): summary is ReactNode {
  if (summary == null) return false
  if (typeof summary === 'string') {
    const s = summary.trim()
    return s.length > 0 && s !== '—'
  }
  return true
}

export function CatalogListCardViewDetail({ label }: { label?: string }) {
  const { t } = useTranslation()
  return (
    <span className={CATALOG_LIST_CARD_CTA_CLASS}>
      <span>{label ?? t('catalog.viewDetail')}</span>
      <ArrowRight size={11} strokeWidth={2} className="transition group-hover:translate-x-0.5" aria-hidden />
    </span>
  )
}

/** 目录卡片统一正文：标题 + 摘要 + 底栏（meta 左 · 查看详情/操作右，无分割线） */
export function CatalogListCardContent({
  className,
  title,
  summary,
  meta,
  footerLabel,
  hideFooter,
  footer,
  footerBlock,
}: CatalogListCardContentProps) {
  const showSummary = shouldShowSummary(summary)
  const showViewDetail = !hideFooter && !footer && !footerBlock
  const showBottomRow = Boolean(meta || footer || showViewDetail)

  return (
    <CatalogListCardBody className={className}>
      <div className="min-w-0 space-y-0.5">
        <h3 className={CATALOG_LIST_CARD_TITLE_CLASS}>{title}</h3>
        {showSummary ? <p className={CATALOG_LIST_CARD_SUMMARY_CLASS}>{summary}</p> : null}
      </div>

      {footerBlock ? (
        <div className="mt-auto space-y-1.5 pt-0.5">{footerBlock}</div>
      ) : showBottomRow ? (
        <div className={CATALOG_LIST_CARD_BOTTOM_ROW_CLASS}>
          {meta ? <div className={CATALOG_LIST_CARD_META_ROW_CLASS}>{meta}</div> : <span className="min-w-0 flex-1" />}
          {footer ?? (showViewDetail ? <CatalogListCardViewDetail label={footerLabel} /> : null)}
        </div>
      ) : null}
    </CatalogListCardBody>
  )
}

export {
  CATALOG_LIST_CARD_BOTTOM_ROW_CLASS,
  CATALOG_LIST_CARD_CTA_CLASS,
  CATALOG_LIST_CARD_META_ROW_CLASS,
  CATALOG_LIST_CARD_SUMMARY_CLASS,
  CATALOG_LIST_CARD_TITLE_CLASS,
}
